import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    ScholarshipStatus,
    ScholarshipStatusEditResource,
    ScholarshipStatusViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipStatusPageHeader } from "./ScholarshipStatusPageHeader";
import { ScholarshipStatusPageBody } from "./ScholarshipStatusPageBody";

const routeName = "scholarshipStatus";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const scholarshipStatusPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Scholarship Status",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type ScholarshipStatusContextValue = {
    onCreate?: (id: number) => void;
};

export const ScholarshipStatusContext = createContext<ScholarshipStatusContextValue>({} as ScholarshipStatusContextValue);

export const ScholarshipStatusPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [scholarshipStatus, setScholarshipStatus] = useState<ScholarshipStatus>({} as ScholarshipStatus);
    const [initScholarshipStatus, setInitScholarshipStatus] = useState<ScholarshipStatus>(scholarshipStatus);
    const [viewResource, setViewResource] = useState<ScholarshipStatusViewResource>({});
    const [editResource, setEditResource] = useState<ScholarshipStatusEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [scholarshipStatus] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.scholarshipStatus.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.scholarshipStatus.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${scholarshipStatusPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.scholarshipStatus.get(id).then((response) => {
                setScholarshipStatus(response);
                setInitScholarshipStatus(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setScholarshipStatus({} as ScholarshipStatus);
            setInitScholarshipStatus({} as ScholarshipStatus);
            resetUnsavedChanges([{} as ScholarshipStatus]);
        }
    };

    const pageHeader = (
        <ScholarshipStatusPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={scholarshipStatusPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            scholarshipStatus={scholarshipStatus}
            setScholarshipStatus={setScholarshipStatus}
            initScholarshipStatus={initScholarshipStatus}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <ScholarshipStatusPageBody
            scholarshipStatus={scholarshipStatus}
            setScholarshipStatus={setScholarshipStatus}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
